// Разбор вставленного текста (без URL) в черновики рейсов.
// Чистый модуль: не импортирует core/store, не делает сетевых запросов.

import type { Currency } from '../types';
import { matchCarrier, TWO_LETTER_CARRIER_CODES } from './carriers';

export interface TextLeg {
  from: string;
  to: string;
  /** 'YYYY-MM-DD' или '' */
  departDate: string;
  /** 'HH:mm' или '' */
  departTime: string;
  arriveDate: string;
  arriveTime: string;
  /** канонический перевозчик или '' */
  carrier: string;
  flightNumber: string;
  priceMinor: number | null;
  currency: Currency;
  notes: string[];
}

export interface TextParseResult {
  legs: TextLeg[];
  notes: string[];
}

function pad2(n: number): string {
  return n < 10 ? `0${n}` : `${n}`;
}

function isoDate(y: number, m: number, d: number): string {
  return `${y.toString().padStart(4, '0')}-${pad2(m)}-${pad2(d)}`;
}

function addDays(iso: string, days: number): string {
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d + days));
  return isoDate(dt.getUTCFullYear(), dt.getUTCMonth() + 1, dt.getUTCDate());
}

function resolveYear(explicit: number | undefined, opts?: { year?: number }): number {
  if (explicit) return explicit;
  if (opts?.year) return opts.year;
  return new Date().getFullYear();
}

// ─────────────────────────── IATA-кандидаты ───────────────────────────

const STOPLIST = new Set([
  'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN',
  'JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC',
  'USD', 'EUR', 'GBP', 'RUB', 'AND', 'THE', 'MIN', 'MAX', 'VIA', 'NEW', 'AIR', 'ONE', 'TWO',
  'FOR', 'ARE', 'WAS', 'HAS', 'HAD', 'NOT', 'BUT', 'CAN', 'GET', 'OUT', 'DAY', 'NOW', 'WAY',
  'WHO', 'WHY', 'TOO', 'YES', 'PER', 'ALL', 'YOU', 'OFF', 'TOP', 'ROW', 'SEE', 'USE', 'ANY',
  'RUN', 'BUY', 'ADD', 'LOT', 'BAG', 'FEE', 'TAX', 'ETC', 'AKA', 'NON', 'AVG', 'ATM',
]);

export function extractIataCandidates(text: string): { code: string; index: number }[] {
  const out: { code: string; index: number }[] = [];
  const re = /\b[A-Z]{3}\b/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text))) {
    if (!STOPLIST.has(m[0])) out.push({ code: m[0], index: m.index });
  }
  return out;
}

// ─────────────────────────── Направление ───────────────────────────

const ARROW_RE = /->|—>|→|>/;

function findSeparatorIndex(text: string): { index: number } | null {
  const arrow = ARROW_RE.exec(text);
  if (arrow) return { index: arrow.index };
  const toWord = /\bto\b/i.exec(text);
  if (toWord) return { index: toWord.index };
  return null;
}

// ─────────────────────────── Время ───────────────────────────

interface TimeMatch {
  index: number;
  hhmm: string;
}

function findTimes(text: string): TimeMatch[] {
  const out: TimeMatch[] = [];
  const re = /\b(\d{1,2}):(\d{2})\s?([ap]m)?\b/gi;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text))) {
    let h = parseInt(m[1], 10);
    const min = parseInt(m[2], 10);
    const ap = m[3]?.toLowerCase();
    if (min > 59) continue;
    if (ap === 'am') {
      if (h === 12) h = 0;
    } else if (ap === 'pm') {
      if (h !== 12) h += 12;
    }
    if (h > 23) continue;
    out.push({ index: m.index, hhmm: `${pad2(h)}:${pad2(min)}` });
  }
  return out;
}

// ─────────────────────────── Даты ───────────────────────────

const MONTHS: Record<string, number> = {
  jan: 1, feb: 2, mar: 3, apr: 4, may: 5, jun: 6,
  jul: 7, aug: 8, sep: 9, oct: 10, nov: 11, dec: 12,
  янв: 1, фев: 2, мар: 3, апр: 4, май: 5, июн: 6,
  июл: 7, авг: 8, сен: 9, окт: 10, ноя: 11, дек: 12,
};

const LETTER_CLASS = 'A-Za-zА-Яа-яЁё';
const MONTH_ALT = Object.keys(MONTHS).join('|');

interface DateMatch {
  index: number;
  iso: string;
}

function findDates(text: string, opts?: { year?: number }): DateMatch[] {
  const out: DateMatch[] = [];

  // "Mon, Dec 28" / "Dec 28, 2026" / "дек 28"
  {
    const re = new RegExp(
      `(?:[${LETTER_CLASS}]{3,10},?\\s+)?(?<![${LETTER_CLASS}])(${MONTH_ALT})(?![${LETTER_CLASS}])\\.?,?\\s+(\\d{1,2})(?:st|nd|rd|th)?(?:,?\\s+(\\d{4}))?`,
      'gi'
    );
    let m: RegExpExecArray | null;
    while ((m = re.exec(text))) {
      const month = MONTHS[m[1].toLowerCase()];
      const day = parseInt(m[2], 10);
      if (!month || day < 1 || day > 31) continue;
      const year = resolveYear(m[3] ? parseInt(m[3], 10) : undefined, opts);
      out.push({ index: m.index, iso: isoDate(year, month, day) });
    }
  }

  // "28 Dec" / "28 дек 2026"
  {
    const re = new RegExp(
      `\\b(\\d{1,2})\\s+(?<![${LETTER_CLASS}])(${MONTH_ALT})(?![${LETTER_CLASS}])\\.?(?:,?\\s+(\\d{4}))?`,
      'gi'
    );
    let m: RegExpExecArray | null;
    while ((m = re.exec(text))) {
      const day = parseInt(m[1], 10);
      const month = MONTHS[m[2].toLowerCase()];
      if (!month || day < 1 || day > 31) continue;
      const year = resolveYear(m[3] ? parseInt(m[3], 10) : undefined, opts);
      out.push({ index: m.index, iso: isoDate(year, month, day) });
    }
  }

  // ISO 2026-12-28
  {
    const re = /\b(\d{4})-(\d{1,2})-(\d{1,2})\b/g;
    let m: RegExpExecArray | null;
    while ((m = re.exec(text))) {
      const year = parseInt(m[1], 10);
      const month = parseInt(m[2], 10);
      const day = parseInt(m[3], 10);
      if (month < 1 || month > 12 || day < 1 || day > 31) continue;
      out.push({ index: m.index, iso: isoDate(year, month, day) });
    }
  }

  // 28.12 / 28.12.2026 / 28/12
  {
    const re = /\b(\d{1,2})[.\/](\d{1,2})(?:[.\/](\d{2,4}))?\b/g;
    let m: RegExpExecArray | null;
    while ((m = re.exec(text))) {
      const day = parseInt(m[1], 10);
      const month = parseInt(m[2], 10);
      if (month < 1 || month > 12 || day < 1 || day > 31) continue;
      let year: number;
      if (m[3]) {
        let y = parseInt(m[3], 10);
        if (y < 100) y += 2000;
        year = y;
      } else {
        year = resolveYear(undefined, opts);
      }
      out.push({ index: m.index, iso: isoDate(year, month, day) });
    }
  }

  out.sort((a, b) => a.index - b.index);
  const dedup: DateMatch[] = [];
  for (const d of out) {
    const prev = dedup[dedup.length - 1];
    if (prev && Math.abs(d.index - prev.index) < 3) continue;
    dedup.push(d);
  }
  return dedup;
}

// ─────────────────────────── Длительность (подсказка) ───────────────────────────

function findDuration(text: string): number | null {
  const en = /\b(\d+)\s*h\s*(\d+)?\s*m?\b/i.exec(text);
  if (en) {
    const h = parseInt(en[1], 10);
    const mm = en[2] ? parseInt(en[2], 10) : 0;
    return h * 60 + mm;
  }
  const ru = /\b(\d+)\s*ч\s*(\d+)?\s*м?\b/i.exec(text);
  if (ru) {
    const h = parseInt(ru[1], 10);
    const mm = ru[2] ? parseInt(ru[2], 10) : 0;
    return h * 60 + mm;
  }
  return null;
}

// ─────────────────────────── Цена ───────────────────────────

function normalizeAmount(raw: string): number {
  return parseFloat(raw.replace(',', '.'));
}

interface PriceMatch {
  index: number;
  priceMinor: number;
  currency: Currency;
}

function findPrice(text: string): PriceMatch | null {
  let best: PriceMatch | null = null;

  const symRe = /([$€])\s?(\d+(?:[.,]\d{1,2})?)/g;
  let m: RegExpExecArray | null;
  while ((m = symRe.exec(text))) {
    const amount = normalizeAmount(m[2]);
    if (Number.isNaN(amount)) continue;
    const currency: Currency = m[1] === '$' ? 'USD' : 'EUR';
    if (!best || m.index < best.index) {
      best = { index: m.index, priceMinor: Math.round(amount * 100), currency };
    }
  }

  const sufRe = /(\d+(?:[.,]\d{1,2})?)\s?(USD|EUR)\b/gi;
  while ((m = sufRe.exec(text))) {
    const amount = normalizeAmount(m[1]);
    if (Number.isNaN(amount)) continue;
    const currency = m[2].toUpperCase() as Currency;
    if (!best || m.index < best.index) {
      best = { index: m.index, priceMinor: Math.round(amount * 100), currency };
    }
  }

  return best;
}

// ─────────────────────────── Номер рейса ───────────────────────────

function findFlightNumber(text: string): string {
  const re = /\b([A-Za-z0-9]{2})\s?(\d{2,4})\b/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text))) {
    const prefix = m[1].toUpperCase();
    if (TWO_LETTER_CARRIER_CODES.has(prefix)) {
      return `${prefix}${m[2]}`;
    }
  }
  return '';
}

// ─────────────────────────── Разбор одного плеча ───────────────────────────

interface Fallback {
  carrier: string;
  priceMinor: number | null;
  currency: Currency;
}

function parseLegChunk(chunk: string, opts: { year?: number } | undefined, fallback: Fallback): TextLeg {
  const notes: string[] = [];
  const candidates = extractIataCandidates(chunk);
  const sep = findSeparatorIndex(chunk);

  let from = '';
  let to = '';
  if (candidates.length >= 2) {
    if (sep) {
      const before = [...candidates].reverse().find((c) => c.index < sep.index);
      const after = candidates.find((c) => c.index > sep.index);
      from = before?.code ?? candidates[0].code;
      to = after?.code ?? candidates[candidates.length - 1].code;
    } else {
      from = candidates[0].code;
      to = candidates[1].code;
    }
  } else if (candidates.length === 1) {
    from = candidates[0].code;
  }

  const times = findTimes(chunk);
  let departTime = '';
  let arriveTime = '';
  if (sep) {
    const before = [...times].reverse().find((t) => t.index < sep.index);
    const after = times.find((t) => t.index > sep.index);
    departTime = before?.hhmm ?? '';
    arriveTime = after?.hhmm ?? '';
  } else {
    departTime = times[0]?.hhmm ?? '';
    arriveTime = times[1]?.hhmm ?? '';
  }

  const dates = findDates(chunk, opts);
  let departDate = '';
  let arriveDate = '';
  if (sep) {
    const before = [...dates].reverse().find((d) => d.index < sep.index);
    const after = dates.find((d) => d.index > sep.index);
    departDate = before?.iso ?? dates[0]?.iso ?? '';
    arriveDate = after?.iso ?? departDate;
  } else {
    departDate = dates[0]?.iso ?? '';
    arriveDate = dates[1]?.iso ?? departDate;
  }
  if (!arriveDate) arriveDate = departDate;

  const durationMin = findDuration(chunk);
  if (departTime && !arriveTime && durationMin != null) {
    const [h, mi] = departTime.split(':').map(Number);
    const total = h * 60 + mi + durationMin;
    const arrH = Math.floor(total / 60) % 24;
    const arrM = total % 60;
    arriveTime = `${pad2(arrH)}:${pad2(arrM)}`;
    if (departDate && total >= 24 * 60) {
      arriveDate = addDays(departDate, Math.floor(total / (24 * 60)));
    }
    notes.push('время прибытия вычислено из длительности перелёта (Travel time)');
  } else if (departTime && arriveTime && durationMin != null && departDate && arriveDate === departDate) {
    const [dh, dm] = departTime.split(':').map(Number);
    const [ah, am] = arriveTime.split(':').map(Number);
    if (ah * 60 + am <= dh * 60 + dm) {
      arriveDate = addDays(departDate, 1);
      notes.push('прилёт после полуночи — дата прилёта сдвинута на +1 день');
    }
  }

  const detectedCarrier = matchCarrier(chunk);
  const carrier = detectedCarrier || fallback.carrier;
  const flightNumber = findFlightNumber(chunk);
  const price = findPrice(chunk);
  const priceMinor = price?.priceMinor ?? fallback.priceMinor;
  const currency = price?.currency ?? fallback.currency;

  return { from, to, departDate, departTime, arriveDate, arriveTime, carrier, flightNumber, priceMinor, currency, notes };
}

// ─────────────────────────── Оркестратор ───────────────────────────

export function parseText(raw: string, opts?: { year?: number }): TextParseResult {
  const text = raw.trim();
  if (!text) return { legs: [], notes: [] };

  const globalCarrier = matchCarrier(text);
  const globalPrice = findPrice(text);
  const fallback: Fallback = {
    carrier: globalCarrier,
    priceMinor: globalPrice?.priceMinor ?? null,
    currency: globalPrice?.currency ?? 'USD',
  };

  const lines = text
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);
  const legLines = lines.filter((l) => extractIataCandidates(l).length >= 1);

  if (lines.length > 1 && legLines.length >= 2) {
    const legs = legLines.map((line) => parseLegChunk(line, opts, fallback));
    return {
      legs,
      notes: [`Текст разбит на ${legs.length} плеча маршрута — по одной строке на каждое.`],
    };
  }

  const candidates = extractIataCandidates(text);
  if (candidates.length >= 3) {
    const legs: TextLeg[] = [];
    for (let i = 0; i < candidates.length - 1; i++) {
      const start = candidates[i].index;
      const end = i + 2 < candidates.length ? candidates[i + 2].index : text.length;
      legs.push(parseLegChunk(text.slice(start, end), opts, fallback));
    }
    return {
      legs,
      notes: [`Найдено ${candidates.length} аэропортов подряд — маршрут разбит на ${legs.length} плеча.`],
    };
  }

  return { legs: [parseLegChunk(text, opts, fallback)], notes: [] };
}
